import React from 'react'
import styled from 'styled-components'
import { ENTITY_COLORS } from 'constants/entityColors'

const KINDS = {
  building: {
    label: 'Building Passport',
    colour: ENTITY_COLORS.bp,
    ink: '#9c3419',
    title: 'One building. This Passport covers a single address.',
  },
  estate: {
    label: 'Estate Passport',
    colour: ENTITY_COLORS.estate,
    ink: '#4c3a99',
    title: 'Several buildings on one site, grouped as an estate.',
  },
  portfolio: {
    label: 'Portfolio Passport',
    colour: ENTITY_COLORS.portfolio,
    ink: '#1f8c97',
    title: 'A collection of buildings and estates you look after.',
  },
}

const SIZES = {
  xs: { tile: 30, glyph: 19, radius: 8, font: 9 },
  sm: { tile: 34, glyph: 22, radius: 9, font: 10 },
  md: { tile: 48, glyph: 32, radius: 13, font: 11.5 },
  lg: { tile: 64, glyph: 42, radius: 16, font: 13 },
}

const Glyph = ({ kind, size, detail }) => {
  if (kind === 'portfolio') {
    return (
      <svg width={size} height={size} viewBox='0 0 34 34' fill='none' aria-hidden='true'>
        <rect x='3' y='14' width='8' height='15' rx='1.2' fill='currentColor' opacity='0.5' />
        <rect x='23' y='17' width='8' height='12' rx='1.2' fill='currentColor' opacity='0.5' />
        <rect x='12.5' y='5' width='9' height='24' rx='1.4' fill='currentColor' />
        <rect x='14.6' y='9' width='2.4' height='2.4' rx='0.5' fill={detail} />
        <rect x='17.9' y='9' width='2.4' height='2.4' rx='0.5' fill={detail} />
        <rect x='14.6' y='13.6' width='2.4' height='2.4' rx='0.5' fill={detail} />
        <rect x='17.9' y='13.6' width='2.4' height='2.4' rx='0.5' fill={detail} />
        <rect x='15.8' y='19.5' width='3.4' height='9.5' rx='0.5' fill={detail} />
      </svg>
    )
  }

  if (kind === 'estate') {
    return (
      <svg width={size} height={size} viewBox='0 0 34 34' fill='none' aria-hidden='true'>
        <rect x='5.5' y='9' width='10.5' height='18' rx='1.4' fill='currentColor' />
        <rect x='18.5' y='13' width='10' height='14' rx='1.4' fill='currentColor' opacity='0.62' />
        <rect x='8' y='12.4' width='2.4' height='2.4' rx='0.5' fill={detail} />
        <rect x='11.4' y='12.4' width='2.4' height='2.4' rx='0.5' fill={detail} />
        <rect x='9.3' y='18.5' width='3.2' height='8.5' rx='0.5' fill={detail} />
        <rect x='21' y='16.4' width='2.2' height='2.2' rx='0.5' fill={detail} />
        <rect x='24' y='16.4' width='2.2' height='2.2' rx='0.5' fill={detail} />
        <rect x='3' y='28.4' width='28' height='2.2' rx='1.1' fill='currentColor' />
      </svg>
    )
  }

  return (
    <svg width={size} height={size} viewBox='0 0 34 34' fill='none' aria-hidden='true'>
      <rect x='10' y='4' width='14' height='25' rx='1.5' fill='currentColor' />
      <rect x='13.1' y='8.4' width='3.2' height='3.2' rx='0.6' fill={detail} />
      <rect x='17.7' y='8.4' width='3.2' height='3.2' rx='0.6' fill={detail} />
      <rect x='13.1' y='13.6' width='3.2' height='3.2' rx='0.6' fill={detail} />
      <rect x='17.7' y='13.6' width='3.2' height='3.2' rx='0.6' fill={detail} />
      <rect x='15' y='20.4' width='4' height='8.6' rx='0.6' fill={detail} />
    </svg>
  )
}

const PassportEmblem = ({ kind = 'building', size = 'md', showLabel = true, tone = 'solid', className }) => {
  const meta = KINDS[kind] || KINDS.building
  const dims = SIZES[size] || SIZES.md

  return (
    <Wrap className={className} title={meta.title}>
      <Tile
        $colour={meta.colour}
        $tile={dims.tile}
        $radius={dims.radius}
        $solid={tone === 'solid'}
        role='img'
        aria-label={meta.label}
      >
        <Glyph kind={kind} size={dims.glyph} detail={tone === 'solid' ? meta.colour : '#ffffff'} />
      </Tile>
      {showLabel && (
        <Label $ink={meta.ink} $font={dims.font}>{meta.label}</Label>
      )}
    </Wrap>
  )
}

export default PassportEmblem

const Wrap = styled.span`
  && {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    opacity: 1;
  }
`

const Tile = styled.span`
  && {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: ${({ $tile }) => $tile}px;
    height: ${({ $tile }) => $tile}px;
    border-radius: ${({ $radius }) => $radius}px;
    background: ${({ $colour, $solid }) => ($solid ? $colour : `${$colour}1f`)};
    color: ${({ $colour, $solid }) => ($solid ? '#ffffff' : $colour)};
    opacity: 1;
  }

  svg {
    display: block;
  }
`

const Label = styled.span`
  && {
    font-size: ${({ $font }) => $font}px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    white-space: nowrap;
    line-height: 1;
    opacity: 1;
    color: ${({ $ink }) => $ink};
  }
`
